/**
 * Bat Cave Admin Page
 *
 * Admin interface for viewing and downloading uploaded document packages.
 * Packages are created via self-service at /batcave
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users, FileText, Download, Clock,
  CheckCircle2, AlertCircle, XCircle, ExternalLink,
  Loader2, Calendar, Mail, Phone, ChevronRight, Archive, Trash2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  getPackages,
  getPackage,
  downloadPackage,
  cancelPackage,
  formatFileSize,
  getStatusColor
} from '@/services/batcaveService';
import type { BatCavePackage, BatCaveDocument } from '@/types/batcave';
import JSZip from 'jszip';

export const BatCaveAdmin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState<BatCavePackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<BatCavePackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [downloadingPackageId, setDownloadingPackageId] = useState<string | null>(null);

  // Fetch packages on mount
  useEffect(() => {
    fetchPackages();
  }, []);

  // Handle packageId from URL
  useEffect(() => {
    const packageId = searchParams.get('packageId');
    if (packageId && packages.length > 0) {
      const pkg = packages.find(p => p.id === packageId);
      if (pkg) {
        handleSelectPackage(pkg.id);
      }
    }
  }, [searchParams, packages]);

  const fetchPackages = async () => {
    try {
      const data = await getPackages();
      setPackages(data);
    } catch (err) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = async (packageId: string) => {
    setLoadingDetail(true);
    try {
      const pkg = await getPackage(packageId);
      setSelectedPackage(pkg);
      setSearchParams({ packageId });
    } catch (err) {
      toast.error('Failed to load package details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedPackage(null);
    setSearchParams({});
  };

  const handleCancelPackage = async (packageId: string) => {
    if (!confirm('Are you sure you want to cancel this package?')) {
return;
}

    try {
      await cancelPackage(packageId);
      toast.success('Package cancelled');
      fetchPackages();
      if (selectedPackage?.id === packageId) {
        handleCloseDetail();
      }
    } catch (err) {
      toast.error('Failed to cancel package');
    }
  };

  const filteredPackages = statusFilter === 'all'
    ? packages
    : packages.filter(p => p.status === statusFilter);

  const handleDownloadPackage = async (packageId: string) => {
    setDownloadingPackageId(packageId);
    try {
      // First get the package details to get the documents
      const pkg = await getPackage(packageId);
      if (!pkg?.documents?.length) {
        toast.error('No files to download');
        return;
      }

      const result = await downloadPackage(packageId);

      if (result.files?.length) {
        // Create ZIP client-side
        const zip = new JSZip();

        for (const file of result.files) {
          if (file.url) {
            const response = await fetch(file.url);
            const blob = await response.blob();
            zip.file(file.fileName, blob);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pkg.name.replace(/[^a-zA-Z0-9]/g, '_')}_documents.zip`;
        a.click();
        URL.revokeObjectURL(url);

        toast.success('Download started');
      }
    } catch (err) {
      toast.error('Failed to download files');
    } finally {
      setDownloadingPackageId(null);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bat Cave</h1>
            <p className="text-gray-600 mt-1">View and download documents from transitioning loan officers</p>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open('/batcave', '_blank')}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Upload Portal
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Packages"
            value={packages.length}
            icon={Archive}
          />
          <StatCard
            label="Active"
            value={packages.filter(p => p.status === 'active' || p.status === 'pending').length}
            icon={Clock}
            color="blue"
          />
          <StatCard
            label="Completed"
            value={packages.filter(p => p.status === 'completed').length}
            icon={CheckCircle2}
            color="green"
          />
          <StatCard
            label="Total Files"
            value={packages.reduce((sum, p) => sum + (p.upload_count || 0), 0)}
            icon={FileText}
            color="amber"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Package List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Uploaded Packages</CardTitle>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : filteredPackages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Archive className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No packages found</p>
                    <p className="text-sm mt-2">
                      Share <a href="/batcave" className="text-amber-600 hover:underline">/batcave</a> with transitioning LOs
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredPackages.map(pkg => (
                      <PackageRow
                        key={pkg.id}
                        package={pkg}
                        isSelected={selectedPackage?.id === pkg.id}
                        onClick={() => handleSelectPackage(pkg.id)}
                        onDownload={handleDownloadPackage}
                        downloading={downloadingPackageId === pkg.id}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Package Detail */}
          <div className="lg:col-span-1">
            {loadingDetail ? (
              <Card>
                <CardContent className="py-12">
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ) : selectedPackage ? (
              <PackageDetail
                package={selectedPackage}
                onClose={handleCloseDetail}
                onCancel={() => handleCancelPackage(selectedPackage.id)}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a package to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-components

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  color?: 'blue' | 'green' | 'amber' | 'gray';
}

const StatCard = ({ label, value, icon: Icon, color = 'gray' }: StatCardProps) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    gray: 'bg-gray-50 text-gray-600'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', colorClasses[color])}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface PackageRowProps {
  package: BatCavePackage;
  isSelected: boolean;
  onClick: () => void;
  onDownload: (packageId: string) => void;
  downloading: boolean;
}

const PackageRow = ({ package: pkg, isSelected, onClick, onDownload, downloading }: PackageRowProps) => {
  const statusIcons: Record<string, React.ElementType> = {
    pending: Clock,
    active: Clock,
    completed: CheckCircle2,
    expired: AlertCircle,
    cancelled: XCircle
  };
  const StatusIcon = statusIcons[pkg.status] || Clock;
  const hasFiles = (pkg.upload_count || 0) > 0;

  // Get recipient names
  const recipientNames = pkg.recipients?.map(r =>
    r.user ? `${r.user.first_name} ${r.user.last_name}` : 'Unknown'
  ).filter(Boolean) || [];

  return (
    <div
      onClick={onClick}
      className={cn(
        "p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md",
        isSelected ? "border-amber-500 bg-amber-50" : "border-gray-200 bg-white hover:border-gray-300"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Header with status */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={getStatusColor(pkg.status)}>{pkg.status}</Badge>
            <span className="text-xs text-gray-500">
              {new Date(pkg.created_at).toLocaleDateString()}
            </span>
          </div>

          {/* Uploader Info - Primary */}
          {pkg.uploader_name ? (
            <div className="mt-2">
              <h3 className="font-semibold text-gray-900">{pkg.uploader_name}</h3>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                {pkg.uploader_email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {pkg.uploader_email}
                  </span>
                )}
                {pkg.uploader_phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {pkg.uploader_phone}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <h3 className="font-medium text-gray-500 mt-2 italic">No uploader info</h3>
          )}

          {/* Recipients */}
          {recipientNames.length > 0 && (
            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <Users className="h-3 w-3" />
              <span>To: {recipientNames.join(', ')}</span>
            </div>
          )}

          {/* File count */}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {pkg.upload_count || 0} file{(pkg.upload_count || 0) !== 1 ? 's' : ''}
              {pkg.total_file_size_bytes ? ` (${formatFileSize(pkg.total_file_size_bytes)})` : ''}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasFiles && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(pkg.id);
              }}
              disabled={downloading}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </>
              )}
            </Button>
          )}
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </div>
      </div>
    </div>
  );
};

interface PackageDetailProps {
  package: BatCavePackage;
  onClose: () => void;
  onCancel: () => void;
}

const PackageDetail = ({ package: pkg, onClose, onCancel }: PackageDetailProps) => {
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownloadFile = async (doc: BatCaveDocument) => {
    setDownloading(doc.id);
    try {
      const result = await downloadPackage(pkg.id, doc.id);
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (err) {
      toast.error('Failed to download file');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!pkg.documents?.length) {
return;
}

    setDownloading('all');
    try {
      const result = await downloadPackage(pkg.id);

      if (result.files?.length) {
        // Create ZIP client-side
        const zip = new JSZip();

        for (const file of result.files) {
          if (file.url) {
            const response = await fetch(file.url);
            const blob = await response.blob();
            zip.file(file.fileName, blob);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pkg.name.replace(/[^a-zA-Z0-9]/g, '_')}_documents.zip`;
        a.click();
        URL.revokeObjectURL(url);

        toast.success('Download started');
      }
    } catch (err) {
      toast.error('Failed to download files');
    } finally {
      setDownloading(null);
    }
  };

  const isActive = pkg.status === 'pending' || pkg.status === 'active';

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{pkg.name}</CardTitle>
            <CardDescription>{pkg.description}</CardDescription>
          </div>
          <Badge className={getStatusColor(pkg.status)}>{pkg.status}</Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Uploader Info */}
        {pkg.uploader_name && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Uploader</h4>
            <p className="font-medium text-gray-900">{pkg.uploader_name}</p>
            {pkg.uploader_email && (
              <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                <Mail className="h-3 w-3" /> {pkg.uploader_email}
              </p>
            )}
            {pkg.uploader_phone && (
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <Phone className="h-3 w-3" /> {pkg.uploader_phone}
              </p>
            )}
          </div>
        )}

        {/* Recipients */}
        {pkg.recipients && pkg.recipients.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Users className="h-4 w-4" /> Recipients ({pkg.recipients.length})
            </h4>
            <div className="space-y-1">
              {pkg.recipients.map(r => (
                <div key={r.id} className="text-sm text-gray-600 flex items-center justify-between">
                  <span>{r.user?.first_name} {r.user?.last_name}</span>
                  {r.notification_sent && (
                    <Badge variant="outline" className="text-xs">Notified</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        {pkg.documents && pkg.documents.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Documents ({pkg.documents.length})
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadAll}
                disabled={downloading !== null}
              >
                {downloading === 'all' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1" /> Download All
                  </>
                )}
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {pkg.documents.map(doc => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {doc.original_file_name}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownloadFile(doc)}
                    disabled={downloading !== null}
                  >
                    {downloading === doc.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Created</span>
            <p className="font-medium">{new Date(pkg.created_at).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="text-gray-500">Expires</span>
            <p className="font-medium">{new Date(pkg.expires_at).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="text-gray-500">Access Count</span>
            <p className="font-medium">{pkg.access_count || 0}</p>
          </div>
          <div>
            <span className="text-gray-500">Total Size</span>
            <p className="font-medium">{formatFileSize(pkg.total_file_size_bytes || 0)}</p>
          </div>
        </div>

        {/* Actions */}
        {isActive && (
          <Button
            variant="outline"
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={onCancel}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Cancel Package
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
